import type { ReactNode } from 'react';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

export interface LoaderProps {
  /**
   * Shows the loader if true, renders `children` if false.
   * Defaults to true.
   */
  isLoading?: boolean;
  /**
   * Size of the loader. 3 sizes supported, to result in 16/24/32 px.
   * Defaults to 'm'.
   */
  size?: 'm' | 'l' | 'xl';
  /**
   * Should the loader be in greyscale rather than colored.
   * Defaults to false.
   */
  isGreyscale?: boolean;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Children prop from React.
   */
  children?: ReactNode;
}

const SIZES = {
  m: 16,
  l: 24,
  xl: 32,
};

const BORDERS = {
  m: 1,
  l: 2,
  xl: 2,
};

interface SpinnerProps {
  size: number;
  border: number;
  showColors: boolean;
}

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const colors = keyframes`
    0% {
        border-color: ${tokens.BRAND_COLORS.cobalt_600};
        border-top-color: transparent;
    }
    25% {
        border-color: ${tokens.BRAND_COLORS.cobalt_500};
        border-top-color: transparent;
    }
    50% {
        border-color: ${tokens.BRAND_COLORS.cobalt_400};
        border-top-color: transparent;
    }
    75% {
        border-color: ${tokens.BRAND_COLORS.cobalt_500};
        border-top-color: transparent;
    }
    100% {
        border-color: ${tokens.BRAND_COLORS.cobalt_600};
        border-top-color: transparent;
    }
`;

const Container = styled.div`
  ${tokens.flexCenter()}
`;

const Spinner = styled('div', {
  shouldForwardProp: (prop: string) =>
    prop !== 'size' && prop !== 'border' && prop !== 'showColors',
})<SpinnerProps>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  margin: 0 auto;
  border: ${({ border }) => border}px solid ${tokens.color.text_default};
  border-top-color: transparent;
  border-radius: 50%;
  display: inline-block;
  transform: rotate(0deg);
  animation: ${spin} 0.4s linear infinite
    ${({ showColors }) =>
      showColors && css`, ${colors} 5.6s ease-in-out infinite`};
`;

/**
 * A loading spinner to let users know that content is being loaded or an action is being processed.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/270597-loading/b/85bb24 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-loader--basic-loader Code documentation}
 *
 * @example
 * <Loader isLoading={isContentLoading}>Content loaded</Loader>
 */
export const Loader = ({
  isLoading = true,
  size = 'm',
  isGreyscale = false,
  className,
  children,
}: LoaderProps): JSX.Element =>
  isLoading ? (
    <Container data-testid="loader" className={className}>
      <Spinner
        size={SIZES[size]}
        border={BORDERS[size]}
        showColors={!isGreyscale}
      />
    </Container>
  ) : children ? (
    <>{children}</>
  ) : null;
